<template>
  <SimpleDialog
    v-bind="$attrs"
    max-width="564"
    @close="close_dialog"
    @click:outside="close_dialog"
  >
    <template #title>
      Настройка отображения элементов
    </template>
    <template #text>
      <div class="">
        Настройте под себя отображение элементов таблицы
      </div>
      <div class="pb-4 mt-4">
        <Checkbox
          :input-value="check_all_model"
          :indeterminate="check_all_model === null"
          label="Выбрать все"
          hide-details
          @change="check_all_model = $event"
        />
        <draggable
          :class="$style.checkboxCol"
          :list="cols_list_local"
          :ghost-class="$style.ghost"
          :key="draggable_upd_key"
          @end="onDragEnd"
        >
          <div
            v-for="(col, key) in cols_list_local"
            :key="`${key}_${draggable_upd_key}`"
            v-show="!col.is_hidden"
          >
            <Checkbox
              v-if="!col.disabled"
              :input-value="optional_cols_model"
              :class="$style.checkbox"
              :value="col"
              :label="col.text"

              prepend-icon="mdi-close"
              hide-details
              @change="optional_cols_model = $event"
            >
              <template #prepend>
                <DragIcon :class="$style.dragIcon" />
              </template>
            </Checkbox>
          </div>
        </draggable>
      </div>
      <Checkbox
        v-if="is_catalog_page"
        :input-value="additional_checkbox"
        :label="additional_checkbox_label"
        hide-details
        @change="handle_change($event)"
      />
    </template>
    <template #actions>
      <v-btn
        depressed
        color="accent darken-4"
        @click="save_cols"
      >
        Сохранить
      </v-btn>
      <v-btn
        text
        @click="cancel"
      >
        Отменить
      </v-btn>
      <v-btn
        text
        class="ml-auto"
        @click="clear_local_storage"
      >
        Колонки по умолчанию
      </v-btn>
    </template>
  </SimpleDialog>

</template>

<script lang="ts">
import {
  Component, Prop, PropSync, Vue,
} from 'vue-property-decorator';
import draggable from 'vuedraggable';
import { OptionalCol } from '@/types';
import { global_store } from '@/store';
import { Products } from '@/modules/products/types';

@Component({
  components: {
    SimpleDialog: () => import('../dialogs/simple/SimpleDialog.vue'),
    Checkbox: () => import('../global/checkbox/Checkbox.vue'),
    DragIcon: () => import('@/assets/icons/drag-icon.svg?inline'),
    draggable,
  },
})
export default class CustomColsDialog extends Vue {
  mounted() {
    this.clear_or_upd_cols();
    this.load_cols('mounting');
  }

  @Prop({ type: String, default: '', required: true })
  storage_name!: string;

  @Prop({ type: Array, default: () => ([]) })
  cols!: OptionalCol[];

  @Prop({ type: Array, default: () => ([]) })
  default_cols!: OptionalCol[];

  @Prop({ type: Boolean, default: false })
  additional_checkbox!: boolean;

  @Prop({ type: String, default: 'false' })
  additional_checkbox_label!: string;

  /**
   * Массив колонок, который используется для drag and drop
   */
  @PropSync('cols_list', { type: Array, default: () => ([]) })
  cols_list_local!: OptionalCol[];

  optional_cols: OptionalCol[] | null = [];

  check_all: null | boolean = false;

  /**
   * нужен чтобы корректно обновлялись индексы в цикле v-checkbox
   * обновляется по событию end компонента draggable
   */
  draggable_upd_key = 0;

  get optional_cols_model() {
    return this.optional_cols;
  }

  set optional_cols_model(cols: OptionalCol[] | null) {
    this.optional_cols = cols;

    /**
     * логика переключения галки "Выбрать все"
     * если единственный элемент - data-table-expand, то false,
     * т. к. массив сбрасывается до ['data-table-expand']
     * */
    if (cols) {
      if (cols.length === this.cols.length) {
        this.check_all = true;
      } else if (!cols.length) {
        this.check_all = false;
      } else {
        this.check_all = null;
      }
    } else {
      this.check_all = false;
    }
  }

  get check_all_model() {
    return this.check_all;
  }

  set check_all_model(val: boolean | null) {
    this.check_all = val;

    if (val) {
      this.optional_cols_model = this.cols_list_local;
    } else {
      this.optional_cols_model = [];
    }
  }

  get is_catalog_page() {
    return this.$route.name === Products.Pages.CATALOG;
  }

  setDefaultCols() {
    const defaultCols = this.default_cols.map((col) => ({
      ...col,
      is_visible: col.is_default_visible !== false,
    }));

    this.cols_list_local = defaultCols;

    localStorage.setItem(this.storage_name, JSON.stringify(defaultCols));
    this.optional_cols_model = defaultCols.filter((col) => col.is_visible);
  }

  load_cols(callType: string) {
    const storageCols = localStorage.getItem(this.storage_name) || [];

    if (!storageCols?.length) {
      this.setDefaultCols();
    } else {
      const cols: OptionalCol[] = JSON.parse(storageCols as string);

      if (callType === 'mounting' && this.colsValuesDiff(cols)) {
        this.setDefaultCols();
      }

      this.cols_list_local = cols;
      this.optional_cols_model = cols.filter((col) => col.is_visible);
    }

    this.rerenderTables();
  }

  save_cols() {
    if (this.optional_cols_model !== null && this.optional_cols_model.length) {
      this.cols_list_local.forEach((list_item) => {
        const opts_arr = this.optional_cols_model ?? [];
        const col = list_item;
        const i = opts_arr.findIndex((el) => el.value === col.value);

        col.is_visible = i >= 0;

        if (this.additional_checkbox && col.is_hidden) {
          col.is_visible = true;
        }
        if (!this.additional_checkbox && col.is_hidden) {
          col.is_visible = false;
        }
      });
    } else {
      this.cols_list_local.forEach((list_item) => {
        list_item.is_visible = false;
      });
    }

    localStorage.setItem(this.storage_name, JSON.stringify(this.cols_list_local));
    this.rerenderTables();
    this.$emit('save_cols');
  }

  colsValuesDiff(cols: OptionalCol[]): boolean {
    const colsValues = cols.map((col: OptionalCol) => col.value);
    const setColsValues = new Set(colsValues);

    for (let i = 0; i < this.default_cols.length; i++) {
      if (!setColsValues.has(this.default_cols[i].value)) {
        return true;
      }
    }

    return false;
  }

  cancel() {
    this.cols_list_local = JSON.parse(localStorage.getItem(this.storage_name) as string) as OptionalCol[];
    this.optional_cols_model = this.cols_list_local.filter((c) => c.is_visible);

    this.$emit('cancel_dialog');
  }

  clear_local_storage() {
    const current_value = global_store.state.table_key;

    const incremented_value = current_value + 1;

    localStorage.removeItem(this.storage_name);
    this.load_cols('clearing');
    global_store.mutations.UPD_CLEAT_LOCALE_STORAGE(true);
    global_store.mutations.UPD_TABLE_KEY(incremented_value);
    this.rerenderTables();
    this.$emit('close');
    this.$emit('clear');
    this.$emit('default_limit');
  }

  rerenderTables() {
    this.$nextTick(() => {
      global_store.mutations.RERENDER_TABLES();
    });
  }

  close_dialog() {
    this.$emit('close');
  }

  clear_or_upd_cols() {
    const stored_cols_JSON = localStorage.getItem(this.storage_name);

    if (stored_cols_JSON !== null) {
      const stored_cols = JSON.parse(stored_cols_JSON) as OptionalCol[];

      if (this.cols_list_local.length !== stored_cols.length) {
        localStorage.removeItem(this.storage_name);
      } else {
        let clear_storage = false;

        for (let i = 0; i < this.cols_list_local.length; i++) {
          if ((this.cols_list_local[i].value === stored_cols[i].value && this.cols_list_local[i].text !== stored_cols[i].text)) {
            clear_storage = true;
            break;
          }
        }

        if (clear_storage) {
          localStorage.removeItem(this.storage_name);
        }
      }
    }
  }

  handle_change(val: boolean) {
    this.$emit('change_checkbox', val);
  }

  onDragEnd() {
    this.draggable_upd_key++;
    this.rerenderTables();
  }
}
</script>

<style module lang="scss">

.dragIcon {
  color: rgba(0, 0, 0, .2);
  margin-top: 4px;
  cursor: move; /* Fallback for all browsers */
}

@supports (cursor: grab) {
  .dragIcon {
    cursor: grab; /* Modern browsers that support grab */
  }
}

.checkboxCol {
  padding-left: 4px;
}

.checkbox {
  &.ghost {
    opacity: 0.5;
    background: rgba(0, 0, 0, .2);
  }
}
</style>
